import { useEffect, ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { toast } from 'sonner';
import { setAuthUserAtom, clearAuthAtom } from '@/stores/auth';
import { getSession, clearSession } from '@/lib/auth/session';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  AUTH_FORBIDDEN_EVENT,
  AUTH_TOKEN_KEY,
  USER_KEY,
} from '@/constants/auth.constants';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider
 *
 * The React reflection of the persisted session. It does NOT poll or auto-expire
 * anything — the API client owns token refresh and decides when a session is
 * truly over (it can silently refresh an expired access token). This provider
 * just keeps the Jotai auth state in sync with session.ts and reacts to the two
 * signals that matter: another tab logging out, and the API client reporting the
 * session ended (refresh exhausted) or an action being forbidden.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const setAuthUser = useSetAtom(setAuthUserAtom);
  const clearAuth = useSetAtom(clearAuthAtom);
  const navigate = useNavigate();

  useEffect(() => {
    // Reconcile the atom with what's actually persisted on mount.
    const session = getSession();
    if (session) setAuthUser(session.user);
    else clearAuth();

    const redirectToLogin = () => {
      clearAuth();
      if (!window.location.pathname.includes('/login')) {
        navigate({ to: '/login', replace: true });
      }
    };

    // API client says the session is over (refresh failed/exhausted).
    const onSessionExpired = () => {
      clearSession();
      redirectToLogin();
    };

    // Cross-tab sync: a login/logout in another tab changes these keys.
    const onStorage = (e: StorageEvent) => {
      if (e.key === AUTH_TOKEN_KEY || e.key === USER_KEY || e.key === null) {
        const next = getSession();
        if (next) setAuthUser(next.user);
        else redirectToLogin();
      }
    };

    // 403 → authenticated but not permitted. Session stays; just notify the user.
    const onForbidden = (e: Event) => {
      const detail = (e as CustomEvent<{ message?: string }>).detail;
      toast.error(detail?.message ?? 'You do not have permission to perform this action.');
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    window.addEventListener('storage', onStorage);
    window.addEventListener(AUTH_FORBIDDEN_EVENT, onForbidden);

    return () => {
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(AUTH_FORBIDDEN_EVENT, onForbidden);
    };
    // Effect wires up window listeners once; setters/navigate are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
}